import { type App, type Ref, nextTick, ref, watch } from "vue";



import type { IArticleWithOptions } from "@/data/commands/customer/AddArticleToCartCmd";
import { Factory } from "@/data/dao/Factory";
import { CustomerDao } from "@/data/dao/service/CustomerDao";
import type { ICustomerArticle, ICustomerOptionGroup } from "@/data/model/Customer";
import { CurrentLocationService } from "@/data/services/CurrentLocationService";
import { Format } from "@/data/util/Format";



import { renderItemIconsHtml } from "@/shared/helpers/AttributeHelper";
import { getNameForCurrentUser, getNameInLocationLanguage } from "@/shared/helpers/ItemNameHelper";
import { clientLanguage } from "@/shared/localization/language";
import { logError } from "@/shared/logging/errors";
import { toMoneyWithSign } from "@/shared/utils/format";
import { type VueComponentInstance, createVueComponent } from "@/shared/utils/vue";



import CurrencyInput from "@/components/elements/CurrencyInput.vue";
import ArticleAttributeList from "@/components/order/ArticleAttributeList.vue";



import { TastierClient } from "@/app/TastierClient";
import OptionGroupTile from "@/app/components/order/OptionGroupTile.vue";
import { OptionGroupRow } from "@/app/elements/OptionGroupRow";
import { PayButton } from "@/app/elements/PayButton";
import { QuantityInput } from "@/app/elements/QuantityInput";
import { globalStores } from "@/app/globalStores";
import { trans } from "@/app/localization/Trans";
import { BasePopover } from "@/app/popover/BasePopover";
import { Helper } from "@/app/ui/Helper";
import { Popup } from "@/app/ui/Popup";


interface IOptionGroupControl {
    getValue(): IArticleWithOptions[];
    isValid(): boolean;
    calculatePrice(): number;
    destroy(): void;
}

export class AddToCardPopover extends BasePopover {
    private $articleInfo: JQuery;
    private $articleImage: JQuery;
    private $articleName: JQuery;
    private $price: JQuery;
    private $scrollable: JQuery;
    private $shortDescription: JQuery;
    private $longDescription: JQuery;
    private $attributeList: JQuery;
    private attributeListComponent: VueComponentInstance<typeof ArticleAttributeList>;
    private $optionGroups: JQuery;

    private $bottom: JQuery;
    private addBtn: PayButton;

    private currentArticle?: ICustomerArticle;
    private quantityInput: QuantityInput;
    private optionGroups: Array<IOptionGroupControl>;

    private $openPriceRow: JQuery;
    private openPriceValue: Ref<number | null>;
    private openPriceInput: App<Element> | null | undefined;

    constructor() {
        super(undefined, false);

        this.$element.addClass("addtocard");

        this.$articleInfo = $('<div class="articleinfo" />').appendTo(this.$body);
        this.$articleImage = $('<img class="image" alt="" />').hide().appendTo(this.$articleInfo);
        this.$articleName = $('<div class="articlename" />').appendTo(this.$articleInfo);
        this.$price = $('<div class="price" />').appendTo(this.$articleInfo);
        this.$shortDescription = $('<div class="shortdescription" />').appendTo(this.$articleInfo);

        this.$scrollable = $('<div class="scrollable" />').appendTo(this.$body);
        this.$longDescription = $('<div class="longdescription" />').appendTo(this.$scrollable);

        this.$attributeList = $('<div class="attributelist" />').appendTo(this.$scrollable);
        this.attributeListComponent = createVueComponent(
            ArticleAttributeList,
            {},
            this.$attributeList[0]
        ).mountedComponent;

        this.$openPriceRow = $(
            `<div class="openpricerow">
                <label for="open-price-input">${trans.addToCartPopover.price}</label>
                <div class="open-price-input-container"></div>
            </div>`
        ).appendTo(this.$scrollable);

        this.openPriceValue = ref(null as number | null);
        watch(this.openPriceValue, () => {
            this.validateBtnAndUpdatePrice();
        });

        this.optionGroups = new Array<IOptionGroupControl>();
        this.$optionGroups = $('<div class="optiongroups" />').appendTo(this.$scrollable);

        this.$bottom = $('<div class="bottom" />').appendTo(this.$body);
        this.quantityInput = new QuantityInput(1).appendTo(this.$bottom);
        this.quantityInput.onChange = () => this.validateBtnAndUpdatePrice();

        this.addBtn = new PayButton(trans.general.add).appendTo(this.$bottom);
        this.addBtn.onClick = async () => {
            this.addBtn.setDisabled(trans.addToCartPopover.adding);

            const options = [] as any[];
            for (const optionGroup of this.optionGroups) {
                options.push(...optionGroup.getValue());
            }

            let openPrice = undefined as number | undefined;
            if (this.currentArticle!.isOpenPriceEnabled) {
                openPrice = this.getOpenPriceInputValue();
            }

            try {
                await TastierClient.context.addArticleToCart(
                    this.currentArticle!.articleId,
                    this.currentArticle!.offerId,
                    this.quantityInput.getValue(),
                    options,
                    openPrice,
                    this.currentArticle!.categoryId
                );
                this.hide();
                this.addBtn.setDisabled();
            } catch (ex) {
                logError(ex);
                Popup.show(trans.cart.failedToAdd);
                this.validateBtnAndUpdatePrice();
            }
        };
    }

    public forArticle(customerArticle: ICustomerArticle): AddToCardPopover {
        this.currentArticle = customerArticle;
        this.destroyOptionGroups();

        this.addBtn.setDisabled();

        this.$articleName.html(
            getNameForCurrentUser(customerArticle) + renderItemIconsHtml(customerArticle.articleId, true)
        );
        this.updateArticleImage(customerArticle);

        if (customerArticle.isOpenPriceEnabled) {
            this.$openPriceRow.show();
            this.$price.html("");
        } else {
            this.$openPriceRow.hide();
            const p = Helper.formatPrice(customerArticle);
            this.$price.html(p === "" ? "" : "CHF " + p);
        }

        const shortDesc = Format.translate(customerArticle.shortDescription, clientLanguage());
        if (shortDesc === undefined) this.$shortDescription.html("").hide();
        else this.$shortDescription.html(Format.htmlEscape(shortDesc)!).show();

        const longDesc = Format.translate(customerArticle.longDescription, clientLanguage());
        if (longDesc === undefined) this.$longDescription.html("").hide();
        else this.$longDescription.html(Format.htmlEscape(longDesc)!).show();

        // the attribute list is rendered by vue on the next tick, so the popover height
        // has to be recalculated once the attributes are actually in the dom
        const articleAttributes = globalStores.articleAttributeStore?.filterAttributesForArticle(customerArticle.articleId);
        this.attributeListComponent.setAttributes(articleAttributes ?? []);
        nextTick(() => this.updateBodyHeight(false));

        const maxArticleQuantityToAdd = this.getMaxQuantity(customerArticle);

        this.quantityInput.setMin(customerArticle.minQuantity ?? 1);
        this.quantityInput.setMax(maxArticleQuantityToAdd);
        this.quantityInput.setValue(1);

        if (maxArticleQuantityToAdd == 1) {
            this.quantityInput.hide();
        } else {
            this.quantityInput.show();
        }

        if (this.currentArticle.hasOptions) {
            this.setFullScreen(true);
            this.loadOptionGroups(customerArticle);
        } else {
            this.setFullScreen(false);
            this.validateBtnAndUpdatePrice();
        }

        return this;
    }

    protected onShow() {
        this.addBtn.setAction(trans.general.add);
        if (this.openPriceInput == null) {
            const props = {
                id: "open-price-input",
                modelValue: this.openPriceValue,
                min: this.currentArticle?.isNegativeOpenPrice === true ? -20 : 0,
                max: this.currentArticle?.isNegativeOpenPrice === true ? 0 : 9_999_999,
            };
            const result = createVueComponent(
                CurrencyInput,
                props,
                document.querySelector(".open-price-input-container")!
            );
            this.openPriceInput = result.componentApp;
        }

        // the header can be taller than the name alone when the article image is shown
        const height = this.$articleInfo.height();
        if (this.fullscreen) {
            this.$scrollable.removeAttr("style").css({
                top: height + 9,
            });
        } else {
            this.$scrollable.removeAttr("style");
        }

        this.updateBodyHeight(false);
    }

    protected onHide() {
        this.openPriceValue.value = null;
        this.$scrollable.removeAttr("style");
    }

    protected onDestroy() {
        this.destroyOptionGroups();
    }

    private updateArticleImage(article: ICustomerArticle) {
        const showItemImage =
            article.imagePath != null &&  (globalStores.identityStore?.hasOperatorAccess != true || (
            globalStores.identityStore?.hasOperatorAccess === true &&
            globalStores.offerStore?.orderService?.useCase?.operatorDisplayItemImages === true));

        if (showItemImage) {
            // the kiosk box is far bigger than the 148x148 thumbnail, so it takes the 700x700
            // kiosk variant like the kiosk item tiles do
            const imagePath =
                globalStores.kioskStore?.isKioskMode === true
                    ? (article.kioskImagePath ?? article.imagePath!)
                    : article.imagePath!;

            this.$articleImage.attr("src", imagePath).show();
            this.$articleInfo.addClass("withimage");
        } else {
            this.$articleImage.removeAttr("src").hide();
            this.$articleInfo.removeClass("withimage");
        }
    }

    private destroyOptionGroups() {
        if (this.optionGroups.length === 0) return;

        for (const optionGroup of this.optionGroups) {
            optionGroup.destroy();
        }

        this.optionGroups = new Array<IOptionGroupControl>();
    }

    private createOptionGroupControl(
        optionGroup: ICustomerOptionGroup,
        mainArticleId: string,
        showPlusOnPrices: boolean
    ): IOptionGroupControl {
        if (globalStores.kioskStore?.isKioskMode === true) {
            return this.createOptionGroupTile(optionGroup, mainArticleId, showPlusOnPrices);
        }

        const group = new OptionGroupRow(optionGroup, mainArticleId, showPlusOnPrices).appendTo(this.$optionGroups);
        group.onChange = () => this.validateBtnAndUpdatePrice();
        return group;
    }

    private createOptionGroupTile(
        optionGroup: ICustomerOptionGroup,
        mainArticleId: string,
        showPlusOnPrices: boolean
    ): IOptionGroupControl {
        const container = $('<div class="optiongroup-tile-host" />').appendTo(this.$optionGroups);
        const { mountedComponent, componentApp } = createVueComponent(
            OptionGroupTile,
            {
                optionGroup,
                mainArticleId,
                showPlusOnPrices,
                onChange: () => this.validateBtnAndUpdatePrice(),
            },
            container[0]
        );

        return {
            getValue: () => mountedComponent.getValue(),
            isValid: () => mountedComponent.isValid(),
            calculatePrice: () => mountedComponent.calculatePrice(),
            destroy: () => {
                componentApp.unmount();
                container.remove();
            },
        };
    }

    private async loadOptionGroups(article: ICustomerArticle) {
        const offer = TastierClient.context.currentOffer;
        // todo: remove this check and ELSE code once we confirm that async loading is not needed
        if (offer?.offerOptionGroups != null) {
            try {
                let optionGroups: ICustomerOptionGroup[] = [];
                if (article.sortedOptionGroups != null) {
                    optionGroups = article.sortedOptionGroups.map((x) => {
                        const offerOptionGroup = offer.offerOptionGroups!.find((y) => y.optionGroupId === x);
                        if (offerOptionGroup != null) {
                            return offerOptionGroup;
                        }

                        throw Error("Offer option group not found!");
                    });
                }

                for (const optionGroup of optionGroups) {
                    this.optionGroups.push(this.createOptionGroupControl(optionGroup, article.articleId, article.price !== 0));
                }

                this.validateBtnAndUpdatePrice();
            } catch (ex) {
                logError(ex);
                Popup.show(trans.addToCartPopover.failedLoadingOptions);
                this.validateBtnAndUpdatePrice();
            }
            return;
        }

        this.$optionGroups.html(`<div class="loading"><div class="loader"></div><div class="lbl">${trans.addToCartPopover.loadingOptions}</div></div>`);
        const orderApplication = Helper.getCurrentOrderServiceOrderApplication();
        try {
            if (orderApplication == null) {
                throw Error("Use case is null!");
            }
            const offerId = TastierClient.context.currentOffer?.offerId;
            if (offerId == null) {
                throw Error("Offer id is null!");
            }
            const locationId = CurrentLocationService.getLocationId();
            if (locationId == null) {
                throw Error("Location id is null!");
            }

            const customerDao = Factory.getInstance<CustomerDao>(CustomerDao);
            const optionGroups = await customerDao.getOptionsOfArticle({
                article: article.articleId,
                offerId: offerId,
                useCaseId: orderApplication.id,
                locationId: locationId,
            });
            this.$optionGroups.empty();

            for (const optionGroup of optionGroups) {
                this.optionGroups.push(this.createOptionGroupControl(optionGroup, article.articleId, article.price !== 0));
            }

            this.validateBtnAndUpdatePrice();
        } catch (ex) {
            logError(ex);
            Popup.show(trans.addToCartPopover.failedLoadingOptions);
            this.validateBtnAndUpdatePrice();
        }
    }

    private getOpenPriceInputValue() {
        const value = this.openPriceValue.value;
        if (value == null || isNaN(value)) return 0;
        else return value;
    }

    private isCorrectOpenPriceEntered(): boolean {
        const value = this.openPriceValue.value;
        return !(value == null || isNaN(value));
    }

    private validateBtnAndUpdatePrice() {
        let isValid = true;
        let unitPrice = this.currentArticle!.isOpenPriceEnabled
            ? this.getOpenPriceInputValue()
            : Helper.sitePrice(this.currentArticle!);

        if (this.currentArticle!.isOpenPriceEnabled) {
            if (!this.isCorrectOpenPriceEntered()) {
                isValid = false;
            }
        }

        const quantity = this.quantityInput.getValue();

        for (const group of this.optionGroups) {
            if (!group.isValid()) isValid = false;

            unitPrice += group.calculatePrice();
        }

        const totalPrice = unitPrice * quantity;
        this.addBtn.setAmount(toMoneyWithSign(totalPrice));

        const maxArticleQuantityToAdd = this.getMaxQuantity(this.currentArticle!);
        if (maxArticleQuantityToAdd == 0) {
            isValid = false;
        }

        if (isValid) {
            this.addBtn.setEnabled();
        } else {
            this.addBtn.setDisabled();
        }
    }

    private getArticleInCartQuantity(article: ICustomerArticle) {
        const currentOffer = TastierClient.context.currentOffer;
        if (currentOffer === undefined || currentOffer.cart === undefined) return 0;

        let totalArticleQuantity = 0;
        for (const item of currentOffer.cart.items) {
            if (item.articleId == article.articleId) {
                totalArticleQuantity += item.quantity;
            }
        }

        return totalArticleQuantity;
    }

    private getTotalCartItems() {
        const currentOffer = TastierClient.context.currentOffer;
        if (currentOffer === undefined || currentOffer.cart === undefined) return 0;

        let totalCartItems = 0;
        for (const item of currentOffer.cart.items) {
            totalCartItems += item.quantity;
        }

        return totalCartItems;
    }

    private getMaxQuantity(article: ICustomerArticle) {
        const articleQuantityCurrentlyInCart = this.getArticleInCartQuantity(article);
        const useCase = Helper.getCurrentOrderServiceOrderApplication();
        const totalCartItems = this.getTotalCartItems();

        const maxTotalPerArticle = article.maxQuantity ?? Number.MAX_VALUE;
        const maxAllowedPerArticle = maxTotalPerArticle - articleQuantityCurrentlyInCart;

        const maxTotalUnitsPerOrder = useCase?.maximumTotalUnitsPerOrder ?? Number.MAX_VALUE;
        const maxAllowedUnitsPerOrder = maxTotalUnitsPerOrder - totalCartItems;

        const max = Math.min(maxAllowedPerArticle, maxAllowedUnitsPerOrder);
        return Math.max(0, max);
    }
}
